import { useEffect, useRef } from 'react'
import { Box } from '@mui/material'
import 'leaflet/dist/leaflet.css'

type GPSPoint = {
    timestamp: string
    latitude: number
    longitude: number
}

type TripMapProps = {
    gpsData: GPSPoint[]
}

const TripMap = ({ gpsData }: TripMapProps) => {
    const mapRef = useRef<HTMLDivElement>(null)
    const mapInstanceRef = useRef<any>(null)
    const isMountedRef = useRef(true)

    useEffect(() => {
        if (!gpsData || gpsData.length === 0) return

        // Reset mounted flag for this effect
        isMountedRef.current = true

        // Add a small delay to ensure DOM is ready
        const timeoutId = setTimeout(() => {
            // Check if component is still mounted
            if (!isMountedRef.current || !mapRef.current) return

            // Load Leaflet dynamically
            const loadMap = async () => {
                try {
                    // Double-check component is still mounted
                    if (!isMountedRef.current) return

                    // console.log('TripMap: Loading map with GPS data:', gpsData.length, 'points')

                    // Dynamically import Leaflet
                    const L = await import('leaflet')

                    // Check again after async import
                    if (!isMountedRef.current) return

                    // Fix for default markers in webpack
                    delete (L.Icon.Default.prototype as any)._getIconUrl
                    L.Icon.Default.mergeOptions({
                        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
                        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
                        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
                    })

                    // Clean up existing map more thoroughly
                    if (mapInstanceRef.current) {
                        try {
                            mapInstanceRef.current.off() // Remove all event listeners
                            mapInstanceRef.current.remove()
                        } catch (e) {
                            console.warn('Error removing existing map:', e)
                        }
                        mapInstanceRef.current = null
                    }

                    // Final check before creating map
                    if (!isMountedRef.current || !mapRef.current || !mapRef.current.parentElement) {
                        console.warn('Component unmounted or container not in DOM, skipping map creation')
                        return
                    }

                    // Clear any existing content
                    mapRef.current.innerHTML = ''

                    // Create map with error handling and disable animations that can cause issues
                    const map = L.map(mapRef.current, {
                        zoomControl: true,
                        attributionControl: true,
                        fadeAnimation: false,
                        zoomAnimation: false,
                        markerZoomAnimation: false
                    }).setView([gpsData[0].latitude, gpsData[0].longitude], 13)

                    // Only store reference if component is still mounted
                    if (isMountedRef.current) {
                        mapInstanceRef.current = map
                    } else {
                        // Component was unmounted during creation, clean up immediately
                        map.remove()
                        return
                    }

                // Check if still mounted before adding layers
                if (!isMountedRef.current) {
                    map.remove()
                    return
                }

                // Add tile layer
                L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                    attribution: '© OpenStreetMap contributors'
                }).addTo(map)

                // Check if still mounted before processing GPS data
                if (!isMountedRef.current) {
                    map.remove()
                    return
                }

                // Create polyline from GPS points (the breadcrumb trail)
                const latLngs: [number, number][] = gpsData.map((point, ) => {
                    const lat = parseFloat(String(point.latitude))
                    const lon = parseFloat(String(point.longitude))
                    return [lat, lon] as [number, number]
                }).filter(([lat, lon]) => !isNaN(lat) && !isNaN(lon))

                const polyline = L.polyline(latLngs, {
                    color: '#ff6b35',
                    weight: 5,
                    opacity: 0.9
                }).addTo(map)

                // Add breadcrumb dots at each GPS point
                // Sample every Nth point to avoid overcrowding (show more points for better visualization)
                const sampleRate = Math.max(1, Math.floor(gpsData.length / 200)) // Show max 200 points

                // let markerCount = 0
                gpsData.forEach((point, index) => {
                    const isStart = index === 0
                    const isEnd = index === gpsData.length - 1

                    // Show start, end, and every Nth point
                    const shouldShow = isStart || isEnd || (index % sampleRate === 0)

                    // Parse coordinates
                    const lat = parseFloat(String(point.latitude))
                    const lon = parseFloat(String(point.longitude))

                    // Skip invalid coordinates
                    if (isNaN(lat) || isNaN(lon)) {
                        return
                    }

                    if (isStart) {
                        // Start marker (green) - always show
                        const startMarker = L.circleMarker([lat, lon], {
                            radius: 10,
                            color: '#4caf50',
                            fillColor: '#4caf50',
                            fillOpacity: 1,
                            weight: 3
                        }).addTo(map)
                        startMarker.bindPopup(`<b>Trip Start</b><br>Time: ${new Date(point.timestamp).toLocaleString()}`)
                        // markerCount++
                    } else if (isEnd) {
                        // End marker (red) - always show
                        const endMarker = L.circleMarker([lat, lon], {
                            radius: 10,
                            color: '#f44336',
                            fillColor: '#f44336',
                            fillOpacity: 1,
                            weight: 3
                        }).addTo(map)
                        endMarker.bindPopup(`<b>Trip End</b><br>Time: ${new Date(point.timestamp).toLocaleString()}`)
                        // markerCount++
                    } else if (shouldShow) {
                        // Breadcrumb dots (orange) - sampled points
                        const breadcrumb = L.circleMarker([lat, lon], {
                            radius: 2,
                            color: '#ff6b35',
                            fillColor: '#ff6b35',
                            fillOpacity: 0.7,
                            weight: 1
                        }).addTo(map)

                        const time = new Date(point.timestamp).toLocaleString()
                        breadcrumb.bindPopup(`<b>Point ${index + 1}</b><br>Time: ${time}`)
                        // markerCount++
                    }
                })

                // Final check before fitting bounds
                if (!isMountedRef.current) {
                    map.remove()
                    return
                }

                // Fit map to show entire route
                if (latLngs.length > 0) {
                    map.fitBounds(polyline.getBounds(), { padding: [20, 20] })
                }

                } catch (error) {
                    console.error('Error loading map:', error)
                }
            }

            loadMap()
        }, 100) // 100ms delay to ensure DOM is ready

        // Cleanup function
        return () => {
            // Clear the timeout if component unmounts before it executes
            clearTimeout(timeoutId)

            // Mark as unmounted to prevent further operations
            isMountedRef.current = false

            // Clean up map instance
            if (mapInstanceRef.current) {
                try {
                    mapInstanceRef.current.off() // Remove all event listeners
                    mapInstanceRef.current.remove()
                } catch (e) {
                    console.warn('Error during map cleanup:', e)
                }
                mapInstanceRef.current = null
            }
        }
    }, [gpsData])

    // Additional cleanup on unmount
    useEffect(() => {
        return () => {
            // Mark as unmounted
            isMountedRef.current = false

            if (mapInstanceRef.current) {
                try {
                    mapInstanceRef.current.off() // Remove all event listeners
                    mapInstanceRef.current.remove()
                } catch (e) {
                    console.warn('Error during component unmount cleanup:', e)
                }
                mapInstanceRef.current = null
            }
        }
    }, [])

    return (
        <Box
            ref={mapRef}
            sx={{
                width: '100%',
                height: '100%',
                minHeight: '300px',
                borderRadius: 1,
                overflow: 'hidden',
                '& .leaflet-container': {
                    height: '100%',
                    width: '100%',
                }
            }}
        />
    )
}

export default TripMap
